import { sleep } from 'utils/CommuteSchedule/utils';

import { api$Masstransit } from 'api';

import debug from 'utils/debug';

const D2 = debug('m:CommuteSchedule:ResolveBuildingCodes');

const resolveBuildingCodes = async (buildingCodes, opts) => {
  const { setProgress = () => {}, ignoreErrors = false } = opts;

  const buildingRequestsResolved = [];
  const buildingRequestsRejected = [];

  const buildingRequests = Object.values(buildingCodes).map(location =>
    api$Masstransit.buildingByCode(location.building_code).then(
      (result) => {
        buildingRequestsResolved.push(result);
      },
      (error) => {
        buildingRequestsRejected.push({
          building_code: location.building_code,
          error,
        });
      }
    )
  );
  D2.S.INFO('buildingRequests', { buildingRequests });

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const progress =
      (buildingRequestsResolved.length + buildingRequestsRejected.length) /
      buildingRequests.length;
    D2.S.INFO('buildingRequests:Progress', {
      buildingRequestsResolved,
      buildingRequestsRejected,
      buildingRequests,
      progress,
    });
    // eslint-disable-next-line no-await-in-loop
    await setProgress('Resolving building codes', progress);
    if (
      buildingRequestsResolved.length + buildingRequestsRejected.length ===
      buildingRequests.length
    ) {
      break;
    }
    // eslint-disable-next-line no-await-in-loop
    await sleep(500);
  }
  D2.S.INFO('buildingRequestsResolved', {
    buildingRequestsResolved,
  });
  D2.S.INFO('buildingRequestsRejected', {
    buildingRequestsRejected,
  });

  const buildingsByCode = buildingRequestsResolved.reduce(
    (acc, record) => {
      const { result } = record;
      const id = `${record.building_code}`;
      if (result.objects && result.objects.length) {
        acc.resolved[id] = result.objects.map(item => ({
          id,
          name: item.name,
          address: item.address,
          blk_no: item.blk_no,
          coordinates: item.point.coordinates,
          code: item.code,
          city: record.city,
          country: record.country,
        }));
      } else {
        acc.failed.push({
          id: record.id,
          building_code: record.building_code,
          error: new Error('No objects returned'),
        });
      }
      return acc;
    },
    { resolved: {}, failed: [] }
  );
  D2.S.INFO('buildingsByCode', {
    buildingsByCode,
  });

  if (!ignoreErrors) {
    const buildingRequestsFailed = [
      ...buildingRequestsRejected,
      ...buildingsByCode.failed,
    ];
    if (buildingRequestsFailed.length) {
      const message = `Failed to resolve building codes: ${buildingRequestsFailed
        .map(item => item.building_code)
        .join(', ')}`;
      D2.S.INFO('Throw', { message, ignoreErrors });
      throw new Error(message);
    }
  }

  return {
    buildingRequestsResolved,
    buildingRequestsRejected,
    buildingsByCode,
  };
};

const commuteSchedule$ResolveBuildingCodes = async (
  currentPassengersData,
  opts
) => {
  const { setProgress = () => {}, ignoreErrors = false } = opts;

  const personBuildingCodes = currentPassengersData
    .reduce((acc, person) => {
      const locations = [person.home, person.office].map(
        location =>
          location.$type === 'building_code' && {
            id: location.building_code,
            building_code: location.building_code,
          }
      );
      return [...acc, ...locations];
    }, [])
    .filter(x => !!x);
  D2.S.INFO('personBuildingCodes', {
    personBuildingCodes,
  });

  const buildingCodes = personBuildingCodes.reduce((acc, location) => {
    return {
      ...acc,
      [location.id]: location,
    };
  }, {});
  D2.S.INFO('buildingCodes', {
    buildingCodes,
  });

  const {
    buildingRequestsResolved,
    buildingRequestsRejected,
    buildingsByCode,
  } = await resolveBuildingCodes(buildingCodes, {
    setProgress,
    ignoreErrors,
  });
  D2.S.INFO('buildingRequestsResolved', {
    buildingRequestsResolved,
  });
  D2.S.INFO('buildingRequestsRejected', {
    buildingRequestsRejected,
  });
  D2.S.INFO('buildingsByCode', { buildingsByCode });

  const dataWithBuildings = currentPassengersData.map((record) => {
    const resolveLocation = location =>
      location.$type === 'building_code'
        ? {
            ...location,
            $buildings: buildingsByCode.resolved[location.id],
          }
        : location;
    return {
      ...record,
      home: resolveLocation(record.home),
      office: resolveLocation(record.office),
    };
  });
  D2.S.INFO('dataWithBuildings', { dataWithBuildings });

  return dataWithBuildings;
};

export default commuteSchedule$ResolveBuildingCodes;
